import React from "react";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { process } from "@progress/kendo-data-query";

export default class QuoteHistoryGrid extends React.Component {
  constructor(props) {
    super(props);
    this._export = null;
    this.state = {
      dataState: {
        skip: 0,
        take: 20,
        sort: [{ field: "dateSubmitted", dir: "desc" }]
      },
      filters: {
        salesRep: null,
        dealer: null,
        quoteNumber: "",
        startDate: null,
        endDate: null
      }
    };
  }

  onDataStateChange = (e) => {
    this.setState({ dataState: e.dataState });
  };

  getFilteredData = () => {
    var data = this.props.data || [];
    var f = this.state.filters;

    if (f.salesRep) {
      data = data.filter(d => d.salesRepNo === f.salesRep);
    }
    if (f.dealer) {
      data = data.filter(d => d.userId === f.dealer);
    }
    if (f.quoteNumber) {
      var q = f.quoteNumber.toLowerCase();
      data = data.filter(d => (d.friendlyId || "").toLowerCase().indexOf(q) > -1);
    }
    if (f.startDate && f.endDate) {
      var start = f.startDate;
      var end = f.endDate;
      data = data.filter(d => {
        if (!d.dateSubmitted) return false;
        var parts = d.dateSubmitted.split("/");
        var dt = new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
        return dt >= start && dt <= end;
      });
    }
    return data;
  };

  onFilterChange = (field, value) => {
    this.setState(prev => ({
      filters: { ...prev.filters, [field]: value },
      dataState: { ...prev.dataState, skip: 0 }
    }));
  };

  onRefresh = () => {
    this.setState({
      dataState: {
        skip: 0,
        take: 20,
        sort: [{ field: "dateSubmitted", dir: "desc" }]
      },
      filters: {
        salesRep: null,
        dealer: null,
        quoteNumber: "",
        startDate: null,
        endDate: null
      }
    });
  };

  onExportExcel = () => {
    if (this._export) {
      var data = this.getFilteredData();
      this._export.save(data);
    }
  };

  getDealerOptions = () => {
    var opts = this.props.filterOptions || {};
    var dealers = opts.dealers || [];
    if (opts.role === "Administrator" && this.state.filters.salesRep) {
      // When a sales rep is selected, narrow dealers to those in the filtered data
      var repEmail = this.state.filters.salesRep;
      var allData = this.props.data || [];
      var userIds = allData
        .filter(d => d.salesRepNo === repEmail && d.userId)
        .map(d => d.userId);
      var uniqueIds = [...new Set(userIds)];
      return dealers.filter(d => uniqueIds.indexOf(d.value) > -1);
    }
    return dealers;
  };

  render() {
    var opts = this.props.filterOptions || {};
    var role = opts.role || "Dealer";
    var salesReps = opts.salesReps || [];
    var dealers = this.getDealerOptions();
    var f = this.state.filters;
    var showSalesRep = role === "Administrator";
    var showDealer = role === "Administrator" || role === "SalesRep";

    var filteredData = this.getFilteredData();
    var result = process(filteredData, this.state.dataState);

    var defaultDropItem = { value: null, text: "All" };

    return (
      <div>
        <div className="quote-history-filters">
          {showSalesRep && (
            <div className="quote-filter-item">
              <label>Sales Rep</label>
              <DropDownList
                data={[defaultDropItem, ...salesReps]}
                textField="text"
                dataItemKey="value"
                value={salesReps.find(s => s.value === f.salesRep) || defaultDropItem}
                onChange={(e) => {
                  var val = e.target.value.value;
                  this.onFilterChange("salesRep", val);
                  // Reset dealer when sales rep changes
                  if (val !== f.salesRep) {
                    this.setState(prev => ({
                      filters: { ...prev.filters, salesRep: val, dealer: null }
                    }));
                  }
                }}
                style={{ width: "200px" }}
              />
            </div>
          )}
          {showDealer && (
            <div className="quote-filter-item">
              <label>Dealer</label>
              <DropDownList
                data={[defaultDropItem, ...dealers]}
                textField="text"
                dataItemKey="value"
                value={dealers.find(d => d.value === f.dealer) || defaultDropItem}
                onChange={(e) => this.onFilterChange("dealer", e.target.value.value)}
                style={{ width: "200px" }}
              />
            </div>
          )}
          <div className="quote-filter-item">
            <label>Quote #</label>
            <input
              type="text"
              className="k-input k-textbox k-input-md k-rounded-md k-input-solid"
              value={f.quoteNumber}
              onChange={(e) => this.onFilterChange("quoteNumber", e.target.value)}
              placeholder="Search..."
              style={{ width: "140px" }}
            />
          </div>
          <div className="quote-filter-item">
            <label>Start Date</label>
            <DatePicker
              value={f.startDate}
              onChange={(e) => this.onFilterChange("startDate", e.value)}
              format="MM/dd/yyyy"
              style={{ width: "160px" }}
            />
          </div>
          <div className="quote-filter-item">
            <label>End Date</label>
            <DatePicker
              value={f.endDate}
              onChange={(e) => this.onFilterChange("endDate", e.value)}
              format="MM/dd/yyyy"
              style={{ width: "160px" }}
            />
          </div>
        </div>

        <ExcelExport
          data={filteredData}
          fileName="QuoteHistory.xlsx"
          ref={(exporter) => { this._export = exporter; }}
        >
          <Grid
            data={result}
            {...this.state.dataState}
            onDataStateChange={this.onDataStateChange}
            sortable={true}
            pageable={{ pageSizes: [10, 20, 50] }}
            cellRender={(cell, cellProps) => {
              if (!cell || !cell.props) return cell;
              const labels = { friendlyId: "Quote #", projectName: "Project Name", company: "Company", dateSubmitted: "Date Submitted", status: "Status", itemCount: "Items", salesRepNo: "Rep #" };
              const label = labels[cellProps.field] || "";
              return React.cloneElement(cell, { ...cell.props, "data-label": label });
            }}
            style={{ fontSize: "14px" }}
          >
            <GridToolbar>
              <button className="k-button k-button-md k-rounded-md k-button-solid" style={{ backgroundColor: "#333", color: "#fff", borderColor: "#333" }} onClick={this.onExportExcel}>
                <span className="k-icon k-i-file-excel"></span>&nbsp;Export to Excel
              </button>
              <button className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={this.onRefresh} style={{ marginLeft: "auto" }}>
                <span className="k-icon k-i-reload"></span>&nbsp;Refresh Grid
              </button>
            </GridToolbar>
            <GridColumn
              field="friendlyId"
              title="Quote #"
              width="120"
              cell={(props) => (
                <td data-label="Quote #">
                  <a href={"/Quote/Detail/" + props.dataItem.id} style={{ textDecoration: "underline" }}>
                    {props.dataItem.friendlyId || props.dataItem.id.substring(0, 8)}
                  </a>
                </td>
              )}
            />
            <GridColumn field="projectName" title="Project Name" />
            <GridColumn field="company" title="Company" />
            <GridColumn field="dateSubmitted" title="Date Submitted" width="130" />
            <GridColumn field="status" title="Status" width="100" />
            <GridColumn field="itemCount" title="Items" width="70" />
            <GridColumn field="salesRepNo" title="Rep #" />
            {/* "Total" column disabled per client ticket 86e3kag2z (2026-10-07).
                ExcelExport takes its columns from the Grid, so the export drops
                it too. The quote rows still carry `total` server-side; to bring
                it back, restore the GridColumn (field="total", right-aligned
                currency) and the label map entry. */}
          </Grid>
        </ExcelExport>
      </div>
    );
  }
}
